import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  BarChart3, Users, UserCheck, Clock, Mail, Send, TrendingUp,
  RefreshCw, Loader2, ArrowRight, BellRing, UserX, CheckCircle,
  Filter, ChevronDown
} from "lucide-react";

const STAT_CARDS = [
  { key: "total", label: "Total Invites", icon: Mail, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "accepted", label: "Signed Up", icon: UserCheck, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "pending", label: "Pending", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
  { key: "expired", label: "Expired", icon: UserX, color: "text-red-500", bg: "bg-red-50" },
];

function FunnelBar({ label, count, pct, color }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-neutral-700">{label}</span>
        <span className="tabular-nums text-neutral-500">{count} <span className="text-xs">({pct}%)</span></span>
      </div>
      <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
    </div>
  );
}

function MiniChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.invited), 1);
  const barW = Math.max(Math.floor(100 / data.length), 2);

  return (
    <div className="flex items-end gap-px h-20 w-full" data-testid="invite-trend-chart">
      {data.slice(-14).map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-px" title={`${d.date}: ${d.invited} invited, ${d.accepted} accepted`}>
          <div className="w-full flex flex-col justify-end" style={{ height: 64 }}>
            <div className="w-full bg-blue-200 rounded-t-sm" style={{ height: Math.max((d.invited / max) * 60, 2) }} />
            {d.accepted > 0 && (
              <div className="w-full bg-emerald-400 rounded-t-sm mt-px" style={{ height: Math.max((d.accepted / max) * 60, 2) }} />
            )}
          </div>
          {i % 3 === 0 && <span className="text-[8px] text-neutral-400 mt-0.5">{d.date.slice(5)}</span>}
        </div>
      ))}
    </div>
  );
}

export function InvitationAnalyticsPanel({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggeringFollowUp, setTriggeringFollowUp] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/invitations/analytics`);
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("Failed to load analytics");
      }
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchAnalytics();
  }, [open]);

  const handleTriggerFollowUps = async () => {
    setTriggeringFollowUp(true);
    try {
      const res = await authFetch(`${API}/consultant/invitations/trigger-follow-ups`, { method: "POST" });
      if (res.ok) {
        toast.success("Follow-up emails processed");
        fetchAnalytics();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Failed");
      }
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
    setTriggeringFollowUp(false);
  };

  if (!open) return null;
  const stats = data?.stats || {};
  const funnel = data?.funnel?.stages || [];
  const effectiveness = data?.follow_up_effectiveness || {};
  const invitations = data?.invitations || [];

  const filtered = statusFilter === "all"
    ? invitations
    : invitations.filter(i => i.status === statusFilter);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="invitation-analytics-panel">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Invitation Analytics
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleTriggerFollowUps} disabled={triggeringFollowUp} data-testid="analytics-follow-up-btn">
                {triggeringFollowUp ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5 mr-1.5" />}
                Send Follow-Ups
              </Button>
              <Button size="sm" variant="ghost" onClick={fetchAnalytics} disabled={loading}>
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading && !data ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
            <div className="space-y-5">

              {/* Stat Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-stat-cards">
                {STAT_CARDS.map(sc => (
                  <Card key={sc.key} className="border-0 shadow-sm">
                    <CardContent className="py-3 px-4 flex items-center gap-3">
                      <div className={`w-10 h-10 ${sc.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <sc.icon className={`w-5 h-5 ${sc.color}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{stats[sc.key] ?? 0}</p>
                        <p className="text-xs text-neutral-500">{sc.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Key Metrics Row */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-3 px-4 text-center">
                    <p className="text-3xl font-bold text-blue-600 tabular-nums">{stats.conversion_rate ?? 0}%</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Conversion Rate</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-3 px-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600 tabular-nums">{stats.avg_response_days ?? 0}d</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Avg Response Time</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-3 px-4 text-center">
                    <p className="text-3xl font-bold text-purple-600 tabular-nums">{stats.follow_ups_sent ?? 0}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Follow-Ups Sent</p>
                  </CardContent>
                </Card>
              </div>

              {/* Funnel + Trend side-by-side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Conversion Funnel */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      Conversion Funnel
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {funnel.length > 0 ? funnel.map((stage, i) => (
                      <FunnelBar
                        key={stage.label}
                        label={stage.label}
                        count={stage.count}
                        pct={stage.pct}
                        color={i === 0 ? "bg-blue-400" : i === 1 ? "bg-amber-400" : "bg-emerald-500"}
                      />
                    )) : (
                      <p className="text-sm text-neutral-400 text-center py-4">No data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* 14-Day Trend */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      14-Day Invite Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4">
                    {data?.time_series ? (
                      <>
                        <MiniChart data={data.time_series} />
                        <div className="flex gap-4 mt-2 text-[10px] text-neutral-400">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-200 rounded-sm" />Invited</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded-sm" />Signed Up</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-neutral-400 text-center py-4">No data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Follow-Up Effectiveness */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Send className="w-4 h-4 text-purple-600" />
                    Follow-Up Effectiveness
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="text-center p-3 bg-emerald-50 rounded-xl">
                      <p className="text-xl font-bold text-emerald-700 tabular-nums">{effectiveness.accepted_no_follow_up ?? 0}</p>
                      <p className="text-[10px] text-emerald-600 mt-0.5">Signed up<br/>without nudge</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-xl">
                      <p className="text-xl font-bold text-blue-700 tabular-nums">{effectiveness.accepted_after_1 ?? 0}</p>
                      <p className="text-[10px] text-blue-600 mt-0.5">Signed up<br/>after 1st email</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-xl">
                      <p className="text-xl font-bold text-purple-700 tabular-nums">{effectiveness.accepted_after_2 ?? 0}</p>
                      <p className="text-[10px] text-purple-600 mt-0.5">Signed up<br/>after 2nd email</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-xl">
                      <p className="text-xl font-bold text-amber-700 tabular-nums">{effectiveness.still_pending_no_follow_up ?? 0}</p>
                      <p className="text-[10px] text-amber-600 mt-0.5">Pending<br/>no follow-up yet</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-xl">
                      <p className="text-xl font-bold text-red-600 tabular-nums">{effectiveness.still_pending_after_follow_ups ?? 0}</p>
                      <p className="text-[10px] text-red-500 mt-0.5">Pending after<br/>follow-ups</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Role & Type Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold">By Role</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-2">
                    {(data?.by_role || []).map(r => (
                      <div key={r.role} className="flex items-center justify-between text-sm">
                        <span className="capitalize text-neutral-600">{r.role.replace(/_/g, " ")}</span>
                        <Badge variant="outline" className="tabular-nums">{r.count}</Badge>
                      </div>
                    ))}
                    {(!data?.by_role || data.by_role.length === 0) && <p className="text-sm text-neutral-400">No invitations yet</p>}
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold">By Invite Method</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-2">
                    {(data?.by_type || []).map(t => (
                      <div key={t.type} className="flex items-center justify-between text-sm">
                        <span className="capitalize text-neutral-600">{t.type.replace(/_/g, " ")}</span>
                        <Badge variant="outline" className="tabular-nums">{t.count}</Badge>
                      </div>
                    ))}
                    {(!data?.by_type || data.by_type.length === 0) && <p className="text-sm text-neutral-400">No invitations yet</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Invitation List */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      Recent Invitations
                    </CardTitle>
                    <div className="flex gap-1">
                      {["all", "pending", "accepted", "expired"].map(f => (
                        <Button
                          key={f}
                          size="sm"
                          variant={statusFilter === f ? "default" : "ghost"}
                          className={`h-7 text-xs ${statusFilter === f ? "bg-neutral-900 text-white" : ""}`}
                          onClick={() => setStatusFilter(f)}
                          data-testid={`filter-${f}`}
                        >
                          {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {filtered.length > 0 ? filtered.map(inv => (
                      <div key={inv.invitation_id} className="flex items-center justify-between p-2.5 bg-neutral-50 rounded-lg text-sm" data-testid="invitation-row">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium truncate">{inv.name || inv.email}</span>
                            {inv.status === "pending" && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>}
                            {inv.status === "accepted" && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Signed Up</Badge>}
                            {inv.status === "expired" && <Badge className="bg-neutral-200 text-neutral-500 text-[10px]">Expired</Badge>}
                            {inv.follow_up_count > 0 && (
                              <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-600">
                                {inv.follow_up_count} follow-up{inv.follow_up_count > 1 ? "s" : ""}
                              </Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-neutral-400 mt-0.5 truncate">
                            {inv.email} &middot; {inv.role?.replace(/_/g, " ")} &middot; {inv.invite_type?.replace(/_/g, " ")}
                            {inv.days_pending !== null && inv.days_pending !== undefined && <> &middot; {inv.days_pending}d pending</>}
                          </p>
                        </div>
                        <span className="text-[10px] text-neutral-400 flex-shrink-0 ml-2">
                          {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""}
                        </span>
                      </div>
                    )) : (
                      <p className="text-sm text-neutral-400 text-center py-6">No invitations match this filter</p>
                    )}
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
